/**
 * Gli indirizzi che cambiano con la lingua: `/it/...` e `/en/...`.
 *
 * Il perche' di un modulo, e non tre righe in `App.js`
 * ---------------------------------------------------
 * La lingua di un sito multilingue e' una cosa, l'indirizzo e' un'altra, e le
 * due si toccano in quattro punti che devono raccontare la stessa storia: il
 * router (quale pagina montare), il selettore della barra (dove portare chi
 * cambia lingua), il canonical che finisce in `<head>` e l'URL che finisce nel
 * sitemap. Se ognuno se la calcola per conto suo, basta che uno traduco lo slug
 * in modo diverso — `local-businesses` contro `attivita-locali` — perche' il
 * canonical dichiari un indirizzo che il router non conosce. E' la forma
 * silenziosa con cui due pagine diventano la stessa pagina per il motore, o
 * nessuna delle due.
 *
 * Che cosa ha un indirizzo proprio e che cosa no
 * ----------------------------------------------
 * Solo le pagine **di marketing**: la home, i prezzi, le attivita' locali, lo
 * spazio commercianti, la directory, il channel manager e i documenti pubblici.
 * Le altre cinque lingue (`es, fr, de, pt, el`) restano su `?lang=xx`, perche'
 * un indirizzo `href="...?lang=es"` dentro un `hreflang` e' esattamente cio' che
 * Google tratta come duplicato: si dichiara una lingua quando esiste una
 * pagina, non quando esiste una traduzione del dizionario.
 *
 * Le rotte tecniche — `/dashboard`, `/guest/...`, `/commercianti/area`,
 * `/p/:slug`, `/citta/:city`, `/struttura/:key` — **non** hanno il prefisso:
 * sono indirizzi che vivono nelle email, nei deep link dell'app, nei
 * `returnTo` dell'autenticazione e nei redirect URI di Google e Apple da anni.
 * Cambiarli per ragioni di SEO significherebbe rompere link che qualcuno ha
 * gia' ricevuto, e un deep link rotto non si recupera con una scansione.
 *
 * La tabella e' specchiata in `backend/locales.py`: il test
 * `test_locale_tables_parity.py` confronta le due e fallisce se una delle due
 * dimentica una pagina o traduce uno slug in modo diverso.
 */

//: Le lingue che hanno un indirizzo. L'ordine e' quello del selettore.
export const URL_LOCALES = ["it", "en"];

//: La lingua di default: `/` porta qui, ed e' l'`x-default` delle pagine.
export const DEFAULT_URL_LOCALE = "it";

/**
 * Una pagina di marketing e il suo slug per lingua. Lo slug vuoto e' la home:
 * `/it` e `/en`, non `/it/home`.
 */
export const MARKETING_PAGES = {
  home: { it: "", en: "" },
  pricing: { it: "prezzi", en: "pricing" },
  localPartners: { it: "attivita-locali", en: "local-businesses" },
  merchants: { it: "commercianti", en: "merchants" },
  directory: { it: "strutture", en: "accommodations" },
  channelManager: { it: "channel-manager", en: "channel-manager" },
  terms: { it: "termini", en: "termini" },
  guestTerms: { it: "condizioni", en: "condizioni" },
  privacy: { it: "privacy", en: "privacy" },
  report: { it: "segnala", en: "segnala" },
};

/** Lo slug di ogni pagina, per lingua. Il primo segmento non si ripete. */
const KEY_BY_SLUG = (() => {
  const map = new Map();
  Object.entries(MARKETING_PAGES).forEach(([key, slugs]) => {
    Object.values(slugs).forEach((slug) => { if (slug) map.set(slug, key); });
  });
  return map;
})();

/** `it` o `en` se l'indirizzo comincia con il prefisso di una lingua, altrimenti `null`. */
export function localeOf(pathname) {
  const match = /^\/(it|en)(?=\/|$)/.exec(String(pathname || ""));
  return match ? match[1] : null;
}

/**
 * La pagina di marketing descritta da un indirizzo, oppure `null`.
 *
 * `{ key, lang }`, dove `lang` e' la lingua del **prefisso** e vale `null`
 * quando l'indirizzo e' quello vecchio senza prefisso (`/prezzi`).
 *
 * Solo il primo segmento conta, e dev'essere l'unico: `/commercianti/area` e'
 * l'area riservata, non la pagina commercianti, e trattarla come marketing
 * porterebbe chi cambia lingua dentro una pagina che non esiste nell'altra.
 */
export function marketingPathOf(pathname) {
  const raw = String(pathname || "/");
  const lang = localeOf(raw);
  const rest = lang ? raw.slice(lang.length + 1) : raw;
  const parts = rest.split("/").filter(Boolean);
  if (!parts.length) return { key: "home", lang };
  if (parts.length > 1) return null;
  const key = KEY_BY_SLUG.get(parts[0]);
  return key ? { key, lang } : null;
}

/** `/en/pricing`, `/it/prezzi`, `/it` per la home. `null` se `key` non esiste. */
export function marketingPath(key, lang) {
  const slugs = MARKETING_PAGES[key];
  if (!slugs) return null;
  const code = URL_LOCALES.includes(lang) ? lang : DEFAULT_URL_LOCALE;
  const slug = slugs[code] ?? slugs[DEFAULT_URL_LOCALE];
  return `/${code}${slug ? `/${slug}` : ""}`;
}

/**
 * L'indirizzo da mettere in un link, che **si porta dietro la lingua**.
 *
 * `marketingPath` dice dove vive una pagina; questo dice dove mandare chi la
 * clicca. La differenza sta tutta nelle cinque lingue senza sezione: da
 * `/en/pricing` chi leggeva in spagnolo cliccava il link dei piani e finiva su
 * `/it/prezzi`, perche' quell'indirizzo non aveva modo di dire `es`. Lo stato
 * del provider restava spagnolo, ma ricaricando la pagina si tornava
 * all'italiano: il link prometteva una lingua e ne consegnava un'altra.
 *
 * Quindi `it`/`en` → il solo indirizzo (`/en/pricing`); ogni altra lingua →
 * l'indirizzo italiano con `?lang=xx` (`/it/prezzi?lang=es`), che e' la forma
 * che `LanguageProvider` legge e che il selettore della barra usa gia'
 * (`marketingLangUrl`).
 *
 * `query` (facoltativo) aggiunge parametri — `{ city: "Roma" }` oppure una
 * stringa gia' pronta — e c'e' perche' non si possa sbagliare concatenando un
 * `?` a mano: `?lang=es` piu' `?city=Roma` farebbe due punti interrogativi, e
 * la ricerca della citta' resterebbe attaccata alla lingua. Un `lang` esplicito
 * dentro `query` vince sul secondo argomento.
 */
export function marketingHref(key, lang, query) {
  const path = marketingPath(key, lang);
  if (!path) return null;
  const params = new URLSearchParams();
  if (query) {
    new URLSearchParams(query).forEach((value, name) => params.set(name, value));
  }
  const code = String(lang || "").trim().toLowerCase();
  if (code && !URL_LOCALES.includes(code) && !params.has("lang")) {
    params.set("lang", code);
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/**
 * La lingua che lo **slug** dichiara da solo, senza guardare il prefisso.
 *
 * Serve agli indirizzi vecchi: `/pricing` senza prefisso e' la pagina prezzi
 * inglese, non un duplicato italiano, e mandarla a `/it/prezzi` direbbe al
 * visitatore che ha sbagliato lingua. Con lo slug come indizio, `/pricing`
 * finisce su `/en/pricing`, che e' esattamente dove puntava.
 */
export function localeOfSlug(pathname) {
  const hit = marketingPathOf(pathname);
  if (!hit) return null;
  const raw = String(pathname || "/");
  const parts = (localeOf(raw) ? raw.slice(localeOf(raw).length + 1) : raw)
    .split("/").filter(Boolean);
  if (!parts.length) return null;
  const slugs = MARKETING_PAGES[hit.key] || {};
  // Quando i due slug coincidono (`termini`, `privacy`, `segnala`) lo slug non
  // dice niente sulla lingua, e fingere che lo dica manderebbe `/termini` alla
  // sezione inglese: in quel caso decide la regola di default.
  if (slugs.it === slugs.en) return null;
  if (slugs.en && slugs.en === parts[0]) return "en";
  if (slugs.it && slugs.it === parts[0]) return "it";
  return null;
}

/**
 * L'indirizzo da usare per `pathname` nella lingua `lang`, se e' una pagina di
 * marketing. `null` per le rotte tecniche: chi chiama decide di non navigare.
 */
export function localisedMarketingPath(pathname, lang) {
  const hit = marketingPathOf(pathname);
  return hit ? marketingPath(hit.key, lang) : null;
}

/**
 * Dove portare il selettore della lingua, con query e ancora della pagina.
 *
 * `it`/`en`: stesso contenuto, altra sezione (`/it/prezzi` → `/en/pricing`).
 * Le altre cinque: stessa sezione, `?lang=xx`, perche' non hanno un indirizzo
 * proprio. `null` sulle rotte tecniche — li' la lingua e' solo una preferenza
 * e la pagina non si sposta.
 */
export function marketingLangUrl(location, lang) {
  const hit = marketingPathOf(location?.pathname);
  if (!hit) return null;
  const params = new URLSearchParams(location?.search || "");
  const hash = location?.hash || "";
  if (URL_LOCALES.includes(lang)) {
    params.delete("lang");
    const query = params.toString();
    return `${marketingPath(hit.key, lang)}${query ? `?${query}` : ""}${hash}`;
  }
  params.set("lang", lang);
  return `${marketingPath(hit.key, hit.lang || DEFAULT_URL_LOCALE)}?${params.toString()}${hash}`;
}

/**
 * La lingua da mostrare quando si apre un indirizzo con prefisso.
 *
 * `null` sulle rotte tecniche: li' vale la preferenza salvata, come prima di
 * questo modulo — l'app non deve cambiare lingua perche' un link portava `/en`.
 */
export function urlLocaleOf(pathname) {
  return marketingPathOf(pathname)?.lang || null;
}
